import React, { type CSSProperties, type JSX } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import ButtonLoader from "./ButtonLoader";

enum ButtonColors {
    white = "white",
    bronze = "bronze",
    green = "green",
    clear = "clear",
    blueGradient = "blueGradient",
    indigo = "indigo",
    clearGray = "clearGray",
    red = "red",
    gray = "gray",
    violet = "violet",
    lightGreen = "lightGreen",
}

const buttonStyles: Record<NonNullable<ButtonProps["color"]>, { button: string; icon: string; disabled: string }> = {
    [ButtonColors.white]: {
        button: "bg-white shadow border border-stone-200 hover:bg-stone-50",
        icon: "stroke-stone-400",
        disabled: "hover:bg-white !cursor-not-allowed",
    },
    [ButtonColors.bronze]: {
        button: "bg-bronze-600 shadow text-white border border-bronze-500 hover:bg-bronze-650 active:bg-bronze-700 shadow-button-bronze",
        icon: "stroke-white",
        disabled: "hover:bg-bronze-500 active:bg-bronze-500 bg-bronze-500 !cursor-not-allowed",
    },
    [ButtonColors.green]: {
        button: "bg-forest-green-950 shadow text-white border border-forest-green-950",
        icon: "stroke-white",
        disabled: "bg-forest-green-300 text-forest-green-200 border-forest-green-300 !cursor-not-allowed",
    },
    [ButtonColors.clear]: {
        button: "bg-transparent hover:bg-stone-50 active:bg-stone-100 border border-transparent",
        icon: "stroke-stone-400",
        disabled: "bg-transparent text-stone-400 border-transparent !cursor-not-allowed",
    },
    [ButtonColors.blueGradient]: {
        button: "bg-gradient-to-r from-[#2563EB] to-[#1E3A8A] text-white shadow border border-blue-500 hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900",
        icon: "stroke-white",
        disabled:
            "bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-900 text-blue-200 border-blue-300 !cursor-not-allowed",
    },
    [ButtonColors.indigo]: {
        button: "bg-indigo-600 shadow text-white border border-indigo-500 hover:bg-indigo-700 active:bg-indigo-800 shadow-indigo-600",
        icon: "stroke-white",
        disabled: "bg-indigo-300 text-indigo-200 border-indigo-300 !cursor-not-allowed",
    },
    [ButtonColors.clearGray]: {
        button: "bg-transparent text-stone-500 border border-stone-200 hover:bg-[rgba(12,10,9,0.02)] hover:text-stone-950",
        icon: "text-stone-400",
        disabled: "bg-transparent text-stone-100 border-stone-100 !cursor-not-allowed",
    },
    [ButtonColors.red]: {
        button: "text-white bg-red-700 shadow hover:bg-red-800",
        icon: "stroke-white",
        disabled: "bg-red-400 !cursor-not-allowed",
    },
    [ButtonColors.gray]: {
        button: "bg-stone-100 shadow border border-stone-200 hover:bg-stone-50",
        icon: "stroke-stone-400",
        disabled: "hover:bg-stone-100 !cursor-not-allowed",
    },
    [ButtonColors.violet]: {
        button: "bg-violet-600 shadow border border-violet-500 hover:bg-violet-500 text-white",
        icon: "stroke-white",
        disabled: "bg-violet-300 hover:bg-violet-300 !cursor-not-allowed",
    },
    [ButtonColors.lightGreen]: {
        button: "bg-green-600 shadow border border-green-500 hover:bg-green-500 text-white",
        icon: "stroke-white",
        disabled: "bg-green-300 hover:bg-green-300 !cursor-not-allowed",
    },
};

// Common styles and button types
const commonStyles = "h-fit font-bold py-[3px] px-2 rounded flex items-center gap-1.5";

export type ButtonProps = React.ComponentPropsWithoutRef<"button"> & {
    icon?: JSX.Element;
    iconClass?: string;
    rightIcon?: JSX.Element;
    rightIconClass?: string;
    id?: string;
    loading?: boolean;
    color?: "white" | "bronze" | "green" | "clear" | "blueGradient" | "indigo" | "clearGray" | "red" | "gray" | "violet" | "lightGreen";
    iconOnThe?: "left" | "right";
    className?: string;
    type?: string;
    href?: string | null;
    target?: string;
    as?: string;
    text?: string | JSX.Element;
    style?: CSSProperties;
    disabled?: boolean;
    textClassName?: string;
    linkClassName?: string;
    rel?: string;
};

const Button = React.forwardRef<HTMLElement, ButtonProps>(function Button(
    {
        icon,
        iconClass,
        rightIcon,
        rightIconClass = "h-4 w-4",
        id,
        loading = false,
        color = "white",
        onClick,
        text,
        target,
        className = "",
        type = "button",
        href = null,
        as = "button",
        style,
        disabled = false,
        textClassName = "",
        linkClassName = "",
        rel,
        ...buttonProps
    },
    ref,
) {
    // Get the color styles based on the color prop, defaulting to white if not provided
    const buttonStyle = buttonStyles[color as ButtonColors] || buttonStyles[ButtonColors.white];

    // Use React.cloneElement to apply the icon className from the color styles directly, then merge with any additional iconClass
    const styledIcon = icon ? (
        <span className={twMerge("inline-flex h-4 w-4 items-center justify-center", iconClass)}>
            {React.cloneElement(icon as React.ReactElement<any>, {
                className: twMerge(buttonStyle.icon, (icon.props as any)?.className),
            })}
        </span>
    ) : null;

    const styledRightIcon = rightIcon ? (
        <span className={twMerge("inline-flex h-4 w-4 items-center justify-center", buttonStyle.icon, rightIconClass)}>
            {React.cloneElement(rightIcon as React.ReactElement<any>, {
                className: twMerge(buttonStyle.icon, (rightIcon.props as any)?.className),
            })}
        </span>
    ) : null;

    // Merge all the classes together based on the color config
    const buttonClasses = twMerge(commonStyles, buttonStyle.button, disabled && buttonStyle.disabled, className);
    const linkClasses = twMerge("flex items-center w-full justify-center", text && "gap-1", linkClassName);

    const textClasses = twMerge("whitespace-nowrap", textClassName);

    // Determine the tag name dynamically based on the "as" prop, defaulting to "button".
    const ButtonComponent = as as React.ElementType<ButtonProps, any>;

    return (
        <ButtonComponent
            {...buttonProps}
            ref={ref}
            id={id}
            type={type}
            onClick={e => {
                // If the button is disabled or loading, do not perform the onClick
                if (disabled || loading) {
                    e.preventDefault();
                    return;
                }
                // Otherwise, execute the onClick that was passed in
                onClick?.(e);
            }}
            className={buttonClasses}
            style={style}
            disabled={disabled}
        >
            {/* If href is provided, render an anchor tag, otherwise render a button */}
            {loading && (
                <div className="px-[23px] py-[6px]">
                    <ButtonLoader variant={color} />
                </div>
            )}
            {!loading && (
                <>
                    {href ? (
                        <Link to={href} target={target} className={linkClasses} rel={rel}>
                            {icon ? styledIcon : null}
                            <span className={textClasses}>{text}</span>
                            {rightIcon ? styledRightIcon : null}
                        </Link>
                    ) : (
                        <>
                            {icon ? styledIcon : null}
                            {text && <span className={textClasses}>{text}</span>}
                            {rightIcon ? styledRightIcon : null}
                        </>
                    )}
                </>
            )}
        </ButtonComponent>
    );
});

export default Button;
